import { useEffect, useState } from "react";
import { Link } from "react-router";
import { api, type LeaderboardEntry } from "../api.ts";
import { usePageMeta } from "../components/Layout.tsx";
import { useSession } from "../session.tsx";

function sinceLabel(iso: string | null): string {
  if (!iso) return "never";
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 30) return `${days}d ago`;
  return `${Math.round(days / 30)}mo ago`;
}

export function Scoreboard() {
  usePageMeta("Scoreboard", "Ranked scores for everyone who chose to be listed.");
  const { user } = useSession();
  const [entries, setEntries] = useState<LeaderboardEntry[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .leaderboard()
      .then((data) => !cancelled && setEntries(data.entries))
      .catch(() => !cancelled && setFailed(true));
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <article className="prose-page">
      <h1>Scoreboard</h1>
      <p className="lede">
        Everyone who chose to be listed, ranked by total score. Only unaided passes score, so this counts
        problems solved without revealing the answer.
      </p>

      {failed && <p className="error">Could not load the scoreboard. Try again in a moment.</p>}
      {!failed && entries === null && <p className="muted">Loading…</p>}

      {entries !== null && entries.length === 0 && (
        <div className="empty">
          <p>Nobody is listed yet.</p>
          <p className="muted">
            The scoreboard is opt-in, so it stays empty until people choose a handle and switch it on.
            {user ? (
              <>
                {" "}
                <Link to="/profile">You can be the first</Link>.
              </>
            ) : (
              <>
                {" "}
                <Link to="/signup">Create an account</Link> to join it.
              </>
            )}
          </p>
        </div>
      )}

      {entries !== null && entries.length > 0 && (
        <div className="table-scroll">
          <table className="board">
            <thead>
              <tr>
                <th>#</th>
                <th>Handle</th>
                <th className="num">Score</th>
                <th className="num">Solved</th>
                <th className="num">Attempts</th>
                <th className="num">Last seen</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <tr key={entry.handle}>
                  <td className="rank">{entry.rank}</td>
                  <td>{entry.handle}</td>
                  <td className="num">{entry.score.toLocaleString()}</td>
                  <td className="num">{entry.solved}</td>
                  <td className="num">{entry.attempts}</td>
                  <td className="num muted">{sinceLabel(entry.lastActiveAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="muted small">
        Listing shows the handle you pick and your scores. It never shows your name or your email. Turn it
        off in <Link to="/profile">your profile</Link> and you disappear from here immediately.
      </p>
    </article>
  );
}
